import {
    Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Badge, IconButton, Menu, MenuButton, MenuList, MenuItem,
    Text, Divider, Tooltip, SkeletonText, Center
} from '@chakra-ui/react';
import { HamburgerIcon, ViewIcon, EditIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import GruntPager from '../custom/GruntPager';

// ── Visine redova headera — koriste se za sticky top offset ──────────────────
const ROW1_H = '28px';
const ROW2_H = '24px';

// ── Zajednički stil za th u prvom redu headera ───────────────────────────────
const th1 = {
    position: 'sticky',
    top: '0',
    zIndex: '5',
    bg: 'white',
    textAlign: 'center',
    fontSize: '2xs',
    color: 'gray.600',
    h: ROW1_H,
    // gornji + donji shadow (donji će biti prekriven drugim redom)
    boxShadow: 'inset 0 1px 0 black',
    borderCollapse: 'separate',
};

// ── Zajednički stil za th u drugom redu headera ──────────────────────────────
const th2 = {
    position: 'sticky',
    top: ROW1_H,
    zIndex: '5',
    bg: 'gray.50',
    textAlign: 'center',
    fontSize: '2xs',
    color: 'gray.500',
    fontWeight: '600',
    h: ROW2_H,
    boxShadow: 'inset 0 -1px 0 black',
};

export default function RegistarTable({
    data,
    onShowItem,
    onEditGovedo,
    tableSize = 'xs',
    onOrderBy,
    odrezano = false,
    dataIsLoading = false,
}) {
    const [prikaz, setPrikaz] = useState({
        orderbycol: 'datum',
        orderby: 'desc',
        rowsperpage: 100,
    });
    const [currPage, setCurrPage] = useState(1);

    useEffect(() => {
        setCurrPage(1);
    }, [data.length]);

    // ── Sortiranje ────────────────────────────────────────────────────────────
    const getOrderImage = (propname) => {
        if (propname.toLowerCase() !== prikaz.orderbycol) return ' ⋮';
        return prikaz.orderby === 'asc' ? ' ▴' : ' ▾';
    };

    const sortData = (column) => {
        const newOrder = prikaz.orderby === 'asc' ? 'desc' : 'asc';
        if (onOrderBy) onOrderBy({ column, asc: newOrder === 'asc' });
        setPrikaz({ ...prikaz, orderbycol: column, orderby: newOrder });
    };

    // ── Pager ─────────────────────────────────────────────────────────────────
    const ShowGruntPager = () => {
        if (data.length > 0 && !dataIsLoading) {
            return (
                <GruntPager
                    currpage={currPage}
                    buttonsize={tableSize}
                    rowcount={data.length}
                    rowsperpage={prikaz.rowsperpage}
                    onClick={(pagenum) => setCurrPage(pagenum)}
                />
            );
        }
    };

    // ── Footer ────────────────────────────────────────────────────────────────
    const getFooter = () => (
        <Box flexDir={'row'} display={'flex'} w="100%" alignItems={'center'} pt={'0.25rem'} pb={'0.25rem'}>
            <Box w="25%">
                <Text fontSize={tableSize}>
                    Prikazano {data.length} zapisa {odrezano?'(rezultat je ograničen - suzite filter)':''}
                </Text>
            </Box>
            <Box w="50%">
                <Center>{ShowGruntPager()}</Center>
            </Box>
            <Box w="25%" />
        </Box>
    );

    // ── Skeleton loading ──────────────────────────────────────────────────────
    const showDataDataIsLoading = () => (
        <Box p={'1rem'}>
            <SkeletonText mt='4' noOfLines={30} spacing='3' skeletonHeight='2' />
        </Box>
    );

    // ── Th helper za klikabilne headere ───────────────────────────────────────
    const ThSort = ({ col, children, style = th1, ...rest }) => (
        <Th
            {...style}
            {...rest}
            onClick={() => sortData(col)}
            cursor={'pointer'}
            _hover={{ bg: 'gray.100' }}
        >
            <>{children}{getOrderImage(col)}</>
        </Th>
    );

    // ── Tablica ───────────────────────────────────────────────────────────────
    const showDataIsLoaded = () => {
        const pageData = data.slice(
            (currPage - 1) * prikaz.rowsperpage,
            currPage * prikaz.rowsperpage
        );

        return (
            <Box display={'flex'} flexDir={'column'} flex={1} h={'100%'} minH={0} w={'100%'}>
                <TableContainer
                    overflowY={'auto'}
                    flex={1} minH={0}
                     className='noscroll'
                    mb={'0.5rem'}
                    css={{ borderCollapse: 'separate' }}
                >
                    <Table
                        variant={'unstyled'}
                        size={tableSize}
                        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
                    >
                        {/* ── HEADER ── */}
                        <Thead>
                            {/* Red 1 — grupni naslovi */}
                            <Tr h={ROW1_H}>
                                {/* Akcija */}
                                <Th {...th1} rowSpan={2} w={'2rem'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                />
                                {/* Rbr */}
                                <Th {...th1} rowSpan={2} w={'2rem'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    #
                                </Th>
                                {/* Životni broj */}
                                <ThSort col="zivbr" style={th1} rowSpan={2} w={'10%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Životni broj
                                </ThSort>
                                {/* Posjednik / Knjiga */}
                                <Th {...th1} rowSpan={2} w={'8%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                    borderLeft={'1px solid'} borderLeftColor={'gray.300'}
                                    onClick={() => sortData('posjednik')} cursor={'pointer'} _hover={{ bg: 'gray.100' }}
                                >
                                    <>Posjednik{getOrderImage('posjednik')}</>
                                </Th>
                                <Th {...th1} rowSpan={2} w={'8%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                    borderRight={'1px solid'} borderRightColor={'gray.300'}
                                    onClick={() => sortData('knjiga')} cursor={'pointer'} _hover={{ bg: 'gray.100' }}
                                >
                                    <>Knjiga{getOrderImage('knjiga')}</>
                                </Th>
                                {/* Pasmina */}
                                <ThSort col="pasmina" style={th1} rowSpan={2}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Pas.
                                </ThSort>
                                {/* Spol */}
                                <ThSort col="spol" style={th1} rowSpan={2} w={'2rem'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Sp.
                                </ThSort>
                                {/* Datum rođenja */}
                                <ThSort col="datum_rodjenje" style={th1} rowSpan={2} w={'7%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Dat. rod.
                                </ThSort>

                                {/* DOLAZAK grupa */}
                                <Th {...th1} colSpan={3}
                                    borderLeft={'1px solid'} borderLeftColor={'grunt2.300'}
                                    borderRight={'1px solid'} borderRightColor={'grunt2.300'}
                                    bg={'grunt2.100'}
                                    color={'grunt2.800'}
                                    onClick={() => sortData('dol_datum')} cursor={'pointer'} _hover={{ bg: 'grunt2.200' }}
                                >
                                    <>Dolazak{getOrderImage('dol_datum')}</>
                                </Th>

                                {/* ODLAZAK grupa */}
                                <Th {...th1} colSpan={3}
                                    borderLeft={'1px solid'} borderLeftColor={'grunt2.400'}
                                    borderRight={'1px solid'} borderRightColor={'grunt2.400'}
                                    bg={'grunt2.300'}
                                    color={'grunt2.800'}
                                    onClick={() => sortData('odl_datum')} cursor={'pointer'} _hover={{ bg: 'grunt2.400' }}
                                >
                                    <>Odlazak{getOrderImage('odl_datum')}</>
                                </Th>

                                {/* Tov */}
                                <ThSort col="dani_u_tovu" style={th1} rowSpan={2} w={'8%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Tov
                                </ThSort>
                                {/* Boks */}
                                <ThSort col="boks" style={th1} rowSpan={2} w={'6%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Boks
                                </ThSort>
                                {/* Putnica */}
                                <ThSort col="putnica_oznaka" style={th1} rowSpan={2} w={'5%'}
                                    boxShadow={'inset 0 1px 0 black, inset 0 -1px 0 black'}
                                >
                                    Putn.
                                </ThSort>
                            </Tr>

                            {/* Red 2 — podnaslovi grupa */}
                            <Tr h={ROW2_H}>
                                {/* Dolazak podstupci */}
                                <Th {...th2}
                                    borderLeft={'1px solid'} borderLeftColor={'grunt2.300'}
                                    bg={'grunt2.100'} color={'grunt2.700'} w={'6%'}
                                >
                                    Datum
                                </Th>
                                <Th {...th2} bg={'grunt2.100'} color={'grunt2.700'} w={'9%'}>
                                    Posjednik
                                </Th>
                                <Th {...th2}
                                    borderRight={'1px solid'} borderRightColor={'grunt2.300'}
                                    bg={'grunt2.100'} color={'grunt2.700'} w={'9%'}
                                >
                                    Knjiga
                                </Th>

                                {/* Odlazak podstupci */}
                                <Th {...th2}
                                    borderLeft={'1px solid'} borderLeftColor={'grunt2.400'}
                                    bg={'grunt2.300'} color={'grunt2.800'} w={'6%'}
                                >
                                    Datum
                                </Th>
                                <Th {...th2} bg={'grunt2.300'} color={'grunt2.800'} w={'9%'}>
                                    Posjednik
                                </Th>
                                <Th {...th2}
                                    borderRight={'1px solid'} borderRightColor={'grunt2.400'}
                                    bg={'grunt2.300'} color={'grunt2.800'} w={'9%'}
                                >
                                    Knjiga
                                </Th>
                            </Tr>
                        </Thead>

                        {/* ── BODY ── */}
                        <Tbody>
                            {pageData.map((item, i) => {
                                const globalIdx = (currPage - 1) * prikaz.rowsperpage + i + 1;
                                const isOdisao = item.odl_datum !== null;
                                const zebra = i % 2 === 0 ? 'white' : 'gray.50';

                                return (
                                    <Tr
                                        key={i}
                                        bg={zebra}
                                        fontWeight={'normal'}
                                        _hover={{ bg: 'grunt2.50', cursor: 'pointer' }}
                                        h={'2rem'}
                                        opacity={isOdisao ? 0.7 : 1}
                                    >
                                        {/* Akcija */}
                                        <Td textAlign={'center'} p={'2px'}>
                                            <Menu size={tableSize} strategy='fixed'>
                                                <MenuButton
                                                    as={IconButton}
                                                    aria-label='Opcije'
                                                    icon={<HamburgerIcon />}
                                                    variant={'outline'}
                                                    size={tableSize}
                                                />
                                                <MenuList>
                                                    <MenuItem
                                                        fontSize={tableSize}
                                                        icon={<ViewIcon boxSize={'14px'} />}
                                                        onClick={() => onShowItem(
                                                            item.govedo_id,
                                                            item.dol_id,
                                                            item.dol_migracija_sifra,
                                                            item.odl_id ?? 0,
                                                            item.odl_id ? item.odl_migracija_sifra : ''
                                                        )}
                                                    >
                                                        Pregled
                                                    </MenuItem>
                                                    <MenuItem
                                                        fontSize={tableSize}
                                                        icon={<EditIcon boxSize={'14px'} />}
                                                        onClick={() => onEditGovedo(item.govedo_id)}
                                                    >
                                                        Uredi podatke goveda
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </Td>

                                        {/* Rbr */}
                                        <Td textAlign={'center'} fontSize={'2xs'} color={'gray.400'}>
                                            {globalIdx}
                                        </Td>

                                        {/* Životni broj */}
                                        <Td
                                            textAlign={'center'}
                                            fontWeight={'semibold'}
                                            fontSize={tableSize}
                                            color={isOdisao ? 'red.500' : 'black'}
                                        >
                                            {item.zivbr}
                                        </Td>

                                        {/* Posjednik / Knjiga (trenutni) */}
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            borderLeft={'1px solid'} borderLeftColor={'gray.200'}>
                                            {item.pos1}
                                        </Td>
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            borderRight={'1px solid'} borderRightColor={'gray.200'}>
                                            {item.knjiga}
                                        </Td>

                                        {/* Pasmina */}
                                        <Td textAlign={'center'} fontSize={'2xs'} fontWeight={'bold'}>
                                            <Tooltip label={item.pasmina}>
                                                <span>{item.pasmina_skraceno}</span>
                                            </Tooltip>
                                        </Td>

                                        {/* Spol */}
                                        <Td textAlign={'center'} fontSize={'2xs'}>
                                            <Badge
                                                rounded={'sm'}
                                                fontWeight={'normal'}
                                                variant={'outline'}
                                                colorScheme={item.spol.toLowerCase() === 'm' ? 'blue' : 'pink'}
                                            >
                                                {item.spol.toUpperCase()}
                                            </Badge>
                                        </Td>

                                        {/* Datum rođenja */}
                                        <Td textAlign={'center'} fontSize={'2xs'} color={'gray.600'}>
                                            {new Date(item.datum_rodjenje).toLocaleDateString('hr-HR')}
                                        </Td>

                                        {/* DOLAZAK */}
                                        <Td textAlign={'center'} fontSize={'2xs'} fontWeight={'bold'}
                                            borderLeft={'1px solid'} borderLeftColor={'grunt2.200'}
                                            bg={i % 2 === 0 ? 'grunt2.50' : 'grunt2.100'}
                                        >
                                            {new Date(item.dol_datum).toLocaleDateString('hr-HR')}
                                        </Td>
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            bg={i % 2 === 0 ? 'grunt2.50' : 'grunt2.100'}
                                        >
                                            {item.pos0}
                                        </Td>
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            borderRight={'1px solid'} borderRightColor={'grunt2.200'}
                                            bg={i % 2 === 0 ? 'grunt2.50' : 'grunt2.100'}
                                        >
                                            {item.knjiga0}
                                        </Td>

                                        {/* ODLAZAK */}
                                        <Td textAlign={'center'} fontSize={'2xs'} fontWeight={'bold'}
                                            borderLeft={'1px solid'} borderLeftColor={'grunt2.300'}
                                            bg={i % 2 === 0 ? 'grunt2.200' : 'grunt2.300'}
                                            color={isOdisao ? 'grunt2.800' : 'gray.300'}
                                        >
                                            {isOdisao
                                                ? new Date(item.odl_datum).toLocaleDateString('hr-HR')
                                                : '—'}
                                        </Td>
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            bg={i % 2 === 0 ? 'grunt2.200' : 'grunt2.300'}
                                        >
                                            {item.odl_novi_posjednik === false
                                                ? <Text color={'red.500'}>{item.odl_migracija}</Text>
                                                : item.pos2}
                                        </Td>
                                        <Td textAlign={'center'} fontSize={'2xs'}
                                            borderRight={'1px solid'} borderRightColor={'grunt2.300'}
                                            bg={i % 2 === 0 ? 'grunt2.200' : 'grunt2.300'}
                                        >
                                            {item.knjiga2}
                                        </Td>

                                        {/* Tov */}
                                        <Td textAlign={'center'} fontSize={'2xs'} fontWeight={'bold'}>
                                            {item.datum_tov_pocetak
                                                ? /*<Tooltip label={new Date(item.datum_tov_pocetak).toLocaleDateString('hr-HR')}>
                                                    <span>{item.dani_u_tovu} d</span>
                                                  </Tooltip>*/
                                                  <span>{new Date(item.datum_tov_pocetak).toLocaleDateString('hr-HR') +' ('+item.dani_u_tovu+' d)'} </span>
                                                : <Text color={'gray.300'}>—</Text>}
                                        </Td>

                                        {/* Boks */}
                                        <Td textAlign={'center'} fontSize={'2xs'}>
                                            <Tooltip label={item.boks_naziv} placement={'left-start'}>
                                                <span>{item.boks ?? '—'}</span>
                                            </Tooltip>
                                        </Td>

                                        {/* Putnica */}
                                        <Td textAlign={'center'} fontWeight={'semibold'} fontSize={'2xs'}>
                                            {item.putnica_oznaka ?? '—'}
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>

                <Divider orientation={'horizontal'} />
                {getFooter()}
            </Box>
        );
    };

    return (
        <Box display={'flex'} flexDir={'column'} flex={1} w='100%' h={'100%'}>
            {dataIsLoading ? showDataDataIsLoading() : showDataIsLoaded()}
        </Box>
    );
}